/** @jsx jsx */

import React, { forwardRef, useEffect, useRef, useState } from 'react';
import { css, jsx } from '@emotion/react';

import type { Ref } from 'react';

import { labelStyles, withModifier } from '@zapier/style-encapsulation';
import { Colors, Typography } from '@/theme';
import { Badge } from '../../display/Badge';
import { Tag } from '../../display/Tag';
import { Link } from '../../navigation/Link';

import { TabsDropdown } from './TabsDropdown';

import type { TabsItemType, TabsSize, OnChooseItem } from './types';
import type { Props as BadgeProps } from '../../display/Badge/Badge';

export type TabsRefElement = HTMLDivElement;

export type Props = {
  /** The `aria-current` value to apply to the selected item. */
  'aria-current'?: 'page' | 'step';
  /**
   * Optional label that provides context as to what the navigation is used for.
   * Used by screen reading software.
   */
  'aria-label'?: string;
  /**
   * React ref that is forwarded along as a prop to facilitate typing and to eliminate use of `forwardRef`.
   */
  forwardedRef?: Ref<TabsRefElement>;
  /** An array of items to render within the navigation. */
  items: Array<TabsItemType>;
  /** Callback for when an item is chosen. */
  onChooseItem?: OnChooseItem;
  /**
   * The `href` of the currently selected navigation item.
   * `href` is used rather than the full item since the item object could be cloned, which ruins `===` checks.
   */
  selectedItemHref: string;
  /**
   * Optional function that will determine if the nav item should be highlighted or not.
   * When not provided, the `href` will be compared to the `selectedItemHref` passed to `Tabs`.
   */
  isItemSelected?: (item: TabsItemType, selectedItemHref: string) => boolean;
  /**
   * Size of navigation.
   * `medium` typically used for small screens and `large` typically used for larger screens.
   */
  size?: TabsSize;
  /**
   * Whether or not the component should render the gray indicator track beneath the nav items.
   */
  shouldRenderTrack?: boolean;
  /* Determines the styling of the `Tabs` items. */
  type?: 'default' | 'brand';
};

const Styles = labelStyles('Tabs', {
  root: (size: Props['size']) => [
    css`
      ${Typography.SmallPrint1}
      position: relative;
      display: grid;
      grid-template-columns: 1fr auto;
      align-items: center;
    `,

    size === 'medium' &&
      withModifier(
        'medium',
        css`
          height: var(--zds-size-medium);
        `
      ),

    size === 'large' &&
      withModifier(
        'large',
        css`
          height: var(--zds-size-large);
        `
      ),
  ],

  primary: ({ count }: { count: number }) => [
    css`
      display: grid;
      height: 100%;
      max-width: 100%;
      min-width: 0; // Allow shrinking.
    `,

    // `minmax(auto, min-content)` behaves such that at the min value of
    // `auto` items could truncate if necessary, and at the max value of
    // `min-content` items will not render any wider than they need to,
    // ensuring that they are left-aligned rather than stretching to fill
    // any leftover space.
    withModifier(
      `count-${count}`,
      css`
        grid-template-columns: repeat(${count}, minmax(auto, min-content));
      `
    ),
  ],

  item: ({ isSelected }: { isSelected: boolean }) => [
    css`
      display: flex;
      align-items: center;
      justify-content: center;
      gap: var(--zds-space-8);
      text-align: center;
      color: var(--zds-text-default);
      text-decoration: none;
      padding: 0 var(--zds-space-16);

      &:not([disabled]):hover,
      &:not([disabled]):focus {
        color: var(--zds-text-default);
        font: var(--zds-small-print-3);
        outline: none;
      }

      &:not([disabled]):focus-visible {
        outline: var(--zds-border-focus);
      }

      &[disabled] {
        color: var(--zds-text-weakest);
        cursor: not-allowed;
      }
    `,
    isSelected &&
      withModifier(
        'selected',
        css`
          color: var(--zds-text-default);
          font: var(--zds-small-print-3);
        `
      ),
  ],

  itemText: css`
    display: block;
    white-space: nowrap;

    /*
      Applies an invisible, bold version of the text in order to prevent a layout shift when hovering over an item.
      https://css-tricks.com/bold-on-hover-without-the-layout-shift/
    */
    &:after {
      display: block;
      content: attr(data-text);
      content: attr(data-text) / '';
      height: 0;
      visibility: hidden;
      overflow: hidden;
      user-select: none;
      pointer-events: none;
      font-weight: var(--zds-semi-bold-inter);

      @media speech {
        display: none;
      }
    }
  `,

  badgeAndTag: ({ isDisabled = false }: { isDisabled: boolean }) => [
    css`
      font-size: var(--zds-font-size-small-print) !important;
    `,
    isDisabled &&
      css`
        background-color: var(--zds-background-stronger) !important;
        color: var(--zds-text-weaker) !important;
      `,
  ],

  indicatorTrack: (shouldRenderTrack: Props['shouldRenderTrack']) => [
    css`
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      /*
        Because there is no 1px size token, we:
        - co-opt zds-size-2 and subtract 1px to get a 1px height for PDL
        - use a 5px fallback and subtract 1px to get a 4px height for legacy
      */
      height: calc(var(--zds-size-2) - 1px);
    `,
    // The track always renders to the DOM to contain the indicator,
    // but it shouldn't always _visibly_ render.
    shouldRenderTrack &&
      withModifier(
        'visible',
        css`
          background-color: ${Colors.GrayWarm3};
        `
      ),
  ],

  indicator: css`
    position: absolute;
    height: var(--zds-size-4);
    /*
      Again, co-opting --zds-size 2 to generate:
      - a top value of -3px for PDL (so that the indicator's bottom aligns with the bottom of the track)
      - a top value of 0px for legacy (so that the indicator appears within the track)
    */
    top: calc(var(--zds-size-2) - 5px);
    border-radius: inherit;
    background-color: var(--zds-ui-primary);
    transition: all 250ms ease-in-out;

    &[data-type='brand'] {
      background-color: var(--zds-ui-brand);
    }
  `,
});

type TabsItemRefElement = HTMLAnchorElement | HTMLDivElement;

type TabsItemProps = TabsItemType & {
  ariaCurrent?: 'page' | 'step';
  onChoose: (event: React.MouseEvent) => void;
  forwardedRef: Ref<TabsItemRefElement> | null;
  isSelected: boolean;
  type: Props['type'];
};

// Since this is a component rather than a DOM node, `forwardRef`
// is used to facilitate properly passing the `ref` along to the DOM node.
const TabsItem = (props: TabsItemProps) => {
  // Render a `button[disabled]` when `props.disabled` to prevent
  // users from tabbing to it while still providing accessible feedback
  // if users navigate to it in another way. Note that `onClick` and
  // `ref` are also not applied since a disabled `NavItem` shouldn't
  // be able to be clicked (`onClick`) or selected (`ref`).
  if (props.disabled) {
    return (
      <button
        type="button"
        aria-label={props.ariaLabel}
        css={Styles.item(props)}
        disabled={true}
        data-zds
      >
        <span css={Styles.itemText} data-zds>
          {props.text}
        </span>
        {props.badgeText && (
          <Badge
            css={Styles.badgeAndTag({
              isDisabled: true,
            })}
            variant={props.badgeVariant}
          >
            {props.badgeText}
          </Badge>
        )}
        {props.tagText && (
          <Tag
            variant={props.tagVariant}
            css={Styles.badgeAndTag({
              isDisabled: true,
            })}
          >
            {props.tagText}
          </Tag>
        )}
      </button>
    );
  }

  let isSelectedBadgeVariant: BadgeProps['variant'] = props.badgeVariant;
  switch (props.badgeVariant) {
    case 'neutral':
      isSelectedBadgeVariant = 'neutral-stronger';
      break;
    case 'primary':
      isSelectedBadgeVariant = 'primary-stronger';
      break;
    case 'brand':
      isSelectedBadgeVariant = 'brand-stronger';
      break;
    case 'success':
      isSelectedBadgeVariant = 'success-stronger';
      break;
    case 'warning':
      isSelectedBadgeVariant = 'warning-stronger';
      break;
    case 'error':
      isSelectedBadgeVariant = 'error-stronger';
      break;
  }

  return (
    <Link
      aria-label={props.ariaLabel}
      aria-current={props.isSelected ? props.ariaCurrent : undefined}
      color={null}
      css={Styles.item(props)}
      href={props.href}
      onClick={props.onChoose}
      ref={props.forwardedRef as Ref<HTMLAnchorElement>}
    >
      <span css={Styles.itemText} data-text={props.text} data-zds>
        {props.text}
      </span>
      {props.badgeText && (
        <Badge
          css={Styles.badgeAndTag({
            type: props.type,
          })}
          variant={
            props.isSelected ? isSelectedBadgeVariant : props.badgeVariant
          }
        >
          {props.badgeText}
        </Badge>
      )}
      {props.tagText && (
        <Tag
          variant={props.tagVariant}
          css={Styles.badgeAndTag({
            type: props.type,
          })}
        >
          {props.tagText}
        </Tag>
      )}
    </Link>
  );
};

/**
 * Creates a horizontal tab navigation component that can display a variable number of items, and allows excess items to be nested into a dropdown menu.
 *
 * `Tabs` updates which item is selected based on its `selectedItemHref` prop.
 */
export const Tabs = forwardRef(
  (
    {
      items,
      selectedItemHref,
      'aria-label': ariaLabel = undefined,
      'aria-current': ariaCurrent = 'page',
      isItemSelected = (item: TabsItemType, selectedItemHref: string) =>
        item.href === selectedItemHref,
      onChooseItem = () => {},
      shouldRenderTrack = true,
      size = 'large',
      type = 'default',
    }: Props,
    ref: React.Ref<HTMLDivElement>
  ) => {
    // Hold onto ref so the selected node can be measured
    // in order to position `indicator` via `indicatorStyles`.
    const selectedItemRef = useRef<TabsItemRefElement>(null);
    const [indicatorStyles, setIndicatorStyles] = useState({});

    const shouldRenderIndicator = !items.every((i) => i.isExtra);

    const primaryItems = items.filter((item) => !item.isExtra);
    const primaryLinks = primaryItems.map((item) => {
      const isSelected = isItemSelected(item, selectedItemHref);
      return (
        <TabsItem
          {...item}
          ariaCurrent={ariaCurrent}
          forwardedRef={
            isSelected && shouldRenderIndicator ? selectedItemRef : null
          }
          isSelected={isSelected}
          key={item.href}
          onChoose={(event) => onChooseItem(item, event)}
          type={type}
        />
      );
    });

    const secondaryItems = items.filter((item) => item.isExtra);
    const isSecondaryItemSelected = secondaryItems.some((item) => {
      return isItemSelected(item, selectedItemHref);
    });
    const secondaryMenu = secondaryItems.length > 0 && (
      <div
        ref={
          isSecondaryItemSelected && shouldRenderIndicator
            ? (selectedItemRef as Ref<HTMLDivElement>)
            : null
        }
        data-zds
      >
        <TabsDropdown
          size={size}
          items={secondaryItems}
          onChooseItem={onChooseItem}
          selectedItemHref={selectedItemHref}
        />
      </div>
    );

    // Whenever `selectedItemHref` changes, read the position of
    // the `current`ly selected node.
    useEffect(() => {
      // `requestAnimationFrame` ensures rendering has completed
      // and measuring is accurate. Without this some measurements
      // may be off on initial render.
      const requestId = requestAnimationFrame(() => {
        setIndicatorStyles(
          selectedItemRef.current
            ? {
                left: selectedItemRef.current!.offsetLeft,
                width: selectedItemRef.current!.offsetWidth,
              }
            : {}
        );
      });
      return () => cancelAnimationFrame(requestId);
    }, [items, selectedItemHref]);

    return (
      <nav aria-label={ariaLabel} css={Styles.root(size)} data-zds>
        <div
          css={Styles.primary({ count: primaryLinks.length })}
          ref={ref}
          data-zds
        >
          {primaryLinks}
        </div>
        {secondaryMenu}
        <div css={Styles.indicatorTrack(shouldRenderTrack)} data-zds>
          <div
            css={Styles.indicator}
            data-type={type}
            style={indicatorStyles}
            data-zds
          />
        </div>
      </nav>
    );
  }
);
